import type { PublicUser } from '@sw/protocol';
import { create } from 'zustand';
import { readJSON, writeJSON } from '../lib/storage';

const KEY = 'sw.session.v1';

export type ConnStatus = 'anonymous' | 'connecting' | 'online' | 'reconnecting' | 'replaced' | 'upgrade';

interface Persisted {
  token: string;
  user: PublicUser;
}

interface SessionState {
  token: string | null;
  user: PublicUser | null;
  status: ConnStatus;
  /** undefined hasta el primer `session:state` del servidor. */
  roomCode: string | null | undefined;
  /** serverTime − Date.now() (para cuentas atrás). */
  serverOffset: number;
  setAuth(token: string, user: PublicUser): void;
  clear(): void;
  setStatus(s: ConnStatus): void;
  setServerState(roomCode: string | null, serverTime: number, user: PublicUser): void;
}

const saved = readJSON<Persisted>(KEY);

export const useSession = create<SessionState>((set) => ({
  token: saved?.token ?? null,
  user: saved?.user ?? null,
  status: saved ? 'connecting' : 'anonymous',
  roomCode: undefined,
  serverOffset: 0,
  setAuth(token, user) {
    writeJSON(KEY, { token, user } satisfies Persisted);
    set({ token, user, status: 'connecting', roomCode: undefined });
  },
  clear() {
    writeJSON(KEY, null);
    set({ token: null, user: null, status: 'anonymous', roomCode: undefined });
  },
  setStatus(status) {
    set({ status });
  },
  setServerState(roomCode, serverTime, user) {
    set((s) => {
      if (s.token) writeJSON(KEY, { token: s.token, user } satisfies Persisted);
      return { roomCode, user, serverOffset: serverTime - Date.now(), status: 'online' };
    });
  },
}));

export const serverNow = () => Date.now() + useSession.getState().serverOffset;
